import { Link } from 'react-router-dom';
import { useState } from 'react';
import './Landing.css';

export default function Landing() {
  const [menuOpen, setMenuOpen] = useState(false);

  return (
    <div className="landing-page">
      {/* ---- NAV ---- */}
      <nav className="landing-nav">
        <div className="landing-container nav-inner">
          <Link to="/" className="logo">
            <img src="/mosaic-icon.png" alt="mosaime" className="logo-icon-img" />
            <span className="logo-text">mosaime</span>
          </Link>

          <div className="nav-links-desktop">
            <Link to="/features" className="nav-link">Features</Link>
            <Link to="/how-it-works" className="nav-link">How It Works</Link>
            <Link to="/philosophy" className="nav-link">Philosophy</Link>
            <Link to="/faq" className="nav-link">FAQ</Link>
            <Link to="/blog" className="nav-link">Blog</Link>
            <Link to="/login" className="nav-link">Log in</Link>
            <Link to="/signup" className="nav-link nav-cta">Get Started</Link>
          </div>

          <button
            className="hamburger"
            aria-label="Open menu"
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen((s) => !s)}
          >
            <span className="hamburger-box">
              <span className="hamburger-inner" />
            </span>
          </button>

          {menuOpen && <div className="menu-backdrop" onClick={() => setMenuOpen(false)} />}
          <div className={`mobile-menu ${menuOpen ? 'open' : ''}`} role="menu">
            <button className="close-btn" onClick={() => setMenuOpen(false)} aria-label="Close menu">×</button>
            <Link to="/features" className="menu-link" onClick={() => setMenuOpen(false)}>Features</Link>
            <Link to="/how-it-works" className="menu-link" onClick={() => setMenuOpen(false)}>How It Works</Link>
            <Link to="/philosophy" className="menu-link" onClick={() => setMenuOpen(false)}>Philosophy</Link>
            <Link to="/faq" className="menu-link" onClick={() => setMenuOpen(false)}>FAQ</Link>
            <Link to="/blog" className="menu-link" onClick={() => setMenuOpen(false)}>Blog</Link>
            <Link to="/login" className="menu-link" onClick={() => setMenuOpen(false)}>Log in</Link>
            <Link to="/signup" className="menu-link" onClick={() => setMenuOpen(false)}>Sign up</Link>
          </div>
        </div>
      </nav>

      {/* ---- HERO ---- */}
      <section className="hero">
        <div className="landing-container hero-inner">
          <h1>Your nutritionist, trainer, and wellness coach — in one text.</h1>
          <p className="hero-sub">
            No app to download. No dashboard to navigate. Text to log meals, workouts, and how
            you're feeling — your coach responds in real time with guidance grounded in your goals.
          </p>
          <div className="hero-cta-row">
            <Link to="/signup" className="btn hero-cta">Start Free — 7-Day Trial</Link>
            <Link to="/how-it-works" className="hero-cta-secondary">See How It Works →</Link>
          </div>
          <p className="hero-note">Cancel anytime.</p>
        </div>
      </section>

      {/* ---- THE MOSAIC ---- */}
      <section className="section mosaic-philosophy-section">
        <div className="landing-container">
          <div className="mosaic-philosophy-layout">
            <div className="mosaic-philosophy-visual">
              <img
                src="/test_chart_300.png"
                alt="Wellness Mosaic chart"
                className="mosaic-philosophy-img"
              />
            </div>
            <div className="mosaic-philosophy-text">
              <h2 className="section-title section-title-left">One picture. Not a scoreboard.</h2>
              <p>
                Most wellness apps bury you in numbers — calorie targets, macro breakdowns, sleep
                efficiency percentages. After a few weeks of obsessively tracking all of it, most
                people burn out and quit, having missed the forest for the trees.
              </p>
              <p>
                mosaime tracks everything under the hood — calories, macros, sleep quality, stress,
                workout performance, mood — but shows you one thing: your Wellness Mosaic. Every
                tile is a choice you made across diet, exercise, and mind. Deeper green reflects
                stronger decisions. A few lighter tiles next to brighter ones isn't a problem to fix
                — that's what balance actually looks like. The goal isn't a perfect score. It's
                watching the whole picture consistently move in the right direction.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ---- WHAT MOSAIME COVERS ---- */}
      <section className="section pillars-section">
        <div className="landing-container">
          <h2 className="section-title">Everything wellness, in one conversation.</h2>
          <p className="section-sub">
            No separate app for nutrition. No separate app for workouts. No separate app for sleep.
          </p>
          <div className="pillars">
            <div className="pillar-card">
              <h3>Diet</h3>
              <p>
                Describe a meal or send a photo — mosaime logs calories, macros, and food quality
                automatically. Get recipes, scan ingredient labels, build meal plans, and generate
                grocery lists, all by text.
              </p>
            </div>
            <div className="pillar-card">
              <h3>Exercise</h3>
              <p>
                Log any workout by describing it. Request a custom training plan and get daily
                workout links by text. Connect Fitbit for passive step, activity, and sleep
                tracking.
              </p>
            </div>
            <div className="pillar-card">
              <h3>Mind</h3>
              <p>
                Share how you slept or how your day is going. mosaime tracks mental wellness
                alongside the physical — and reaches out proactively with check-ins, breathing
                exercises, and nudges.
              </p>
            </div>
          </div>
          <p className="pillars-link">
            <Link to="/features">See everything mosaime can do →</Link>
          </p>
        </div>
      </section>

      {/* ---- PHILOSOPHY TRUST SIGNAL ---- */}
      <section className="section philosophy-trust-section">
        <div className="landing-container philosophy-trust-inner">
          <h2 className="section-title">Coaching anchored to a real philosophy — not a black box.</h2>
          <p>
            mosaime's guidance isn't generated by a generic AI pointed vaguely at "health." Every
            reply, nudge, and coaching brief is grounded in a single, published wellness framework —
            the same philosophy behind everything mosaime does. You can read the whole thing before
            you sign up.
          </p>
          <p className="philosophy-trust-link">
            <Link to="/philosophy">Read our wellness philosophy →</Link>
          </p>
        </div>
      </section>

      {/* ---- FOOTER CTA ---- */}
      <section className="section cta-section">
        <div className="landing-container cta-inner">
          <h2>Start your wellness journey today.</h2>
          <p className="cta-sub">Set up in 5 minutes. Your first 7 days are free.</p>
          <Link to="/signup" className="btn hero-cta">Get Started Free</Link>
          <p className="cta-fine-print">
            7-day free trial · Cancel anytime · SMS rates from your carrier may apply
          </p>
        </div>
      </section>

      {/* ---- FOOTER ---- */}
      <footer className="landing-footer">
        <div className="landing-container footer-inner">
          <div className="footer-brand">
            <img src="/mosaic-icon.png" alt="mosaime" className="logo-icon-img footer-logo-icon-img" />
            <span>mosaime</span>
          </div>
          <div className="footer-links">
            <Link to="/features">Features</Link>
            <Link to="/how-it-works">How It Works</Link>
            <Link to="/philosophy">Philosophy</Link>
            <Link to="/faq">FAQ</Link>
            <Link to="/blog">Blog</Link>
            <Link to="/login">Log in</Link>
            <Link to="/signup">Sign up</Link>
          </div>
          <p className="footer-copy">&copy; {new Date().getFullYear()} MOSAIME LLC. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
